import { GNOMUX_ROOT_ID } from "@db/objects";
import { deleteErrorObject, moveToTrash, restoreFromTrash } from "@db/trash";
import { moveObject, renameObject } from "@db/tree";

export type MoveOperation = {
  kind: "move";
  objectId: string;
  previousParent: string;
  newParent: string;
};

export type RenameOperation = {
  kind: "rename";
  objectId: string;
  previousName: string;
  newName: string;
};

export type DeleteOperation = {
  kind: "delete";
  objectId: string;
  previousParent: string;
};

export type RestoreOperation = {
  kind: "restore";
  objectId: string;
  restoredToParent: string;
};

export type CreateDirectoryOperation = {
  kind: "createDirectory";
  objectId: string;
  parent: string;
  name: string;
};

export type CreateCollectionOperation = {
  kind: "createCollection";
  objectId: string;
  parent: string;
  sequenceIds: string[];
  name: string;
};

export type DuplicateOperation = {
  kind: "duplicate";
  originalId: string;
  createdFileId: string;
  createdSequenceIds: string[];
};

export type TreeOperation =
  | MoveOperation
  | RenameOperation
  | DeleteOperation
  | RestoreOperation
  | CreateDirectoryOperation
  | CreateCollectionOperation
  | DuplicateOperation;

/**
 * Invert a tree operation so it can be undone.
 */
export function invertOperation(operation: TreeOperation): TreeOperation {
  switch (operation.kind) {
    case "move":
      return {
        kind: "move",
        objectId: operation.objectId,
        previousParent: operation.newParent,
        newParent: operation.previousParent,
      };

    case "rename":
      return {
        kind: "rename",
        objectId: operation.objectId,
        previousName: operation.newName,
        newName: operation.previousName,
      };

    case "delete":
      return {
        kind: "restore",
        objectId: operation.objectId,
        restoredToParent: operation.previousParent,
      };

    case "restore":
      return {
        kind: "delete",
        objectId: operation.objectId,
        previousParent: operation.restoredToParent,
      };

    case "createDirectory":
      return {
        kind: "deleteCreated",
        objectId: operation.objectId,
      } as unknown as TreeOperation;

    case "createCollection":
      return {
        kind: "deleteCreated",
        objectId: operation.objectId,
      } as unknown as TreeOperation;

    case "duplicate":
      return {
        kind: "deleteDuplicate",
        createdFileId: operation.createdFileId,
      } as unknown as TreeOperation;

    default:
      throw new Error(
        `Unknown operation kind: ${(operation as TreeOperation).kind}`,
      );
  }
}

type DeleteCreatedOperation = {
  kind: "deleteCreated";
  objectId: string;
};

type DeleteDuplicateOperation = {
  kind: "deleteDuplicate";
  createdFileId: string;
};

type InternalOperation =
  | TreeOperation
  | DeleteCreatedOperation
  | DeleteDuplicateOperation;

/**
 * Execute a tree operation against the database.
 */
export async function executeOperation(
  operation: InternalOperation,
): Promise<void> {
  switch (operation.kind) {
    case "move":
      await moveObject(operation.objectId, operation.newParent);
      break;

    case "rename":
      await renameObject(operation.objectId, operation.newName);
      break;

    case "delete":
      await moveToTrash(operation.objectId, false);
      break;

    case "restore": {
      const result = await restoreFromTrash(
        operation.objectId,
        undefined,
        false,
      );
      if (!result.restored) {
        await restoreFromTrash(operation.objectId, GNOMUX_ROOT_ID, false);
      }
      break;
    }

    case "createDirectory":
    case "createCollection":
      // These are recorded after creation - no execution needed during redo
      // They would only appear as redo after an undo of deleteCreated
      break;

    case "deleteCreated":
      await deleteErrorObject(operation.objectId);
      break;

    case "deleteDuplicate":
      await deleteErrorObject(operation.createdFileId);
      break;

    case "duplicate":
      // Duplicates are recorded after creation - no execution needed during redo
      break;

    default:
      throw new Error(
        `Unknown operation kind: ${(operation as InternalOperation).kind}`,
      );
  }
}

/**
 * Get all object IDs referenced by a tree operation.
 */
export function getOperationObjectIds(operation: TreeOperation): string[] {
  switch (operation.kind) {
    case "move":
    case "rename":
    case "delete":
    case "restore":
      return [operation.objectId];

    case "createDirectory":
    case "createCollection":
      return [operation.objectId];

    case "duplicate":
      return [operation.createdFileId];

    default:
      return [];
  }
}
